import { Controller } from "@hotwired/stimulus"

// Mobile-only sticky "pay now" bar for the cart page.
//
// WHY THIS EXISTS
//
// carts/show is a `lg:grid lg:grid-cols-3`. On desktop the payment section is
// the right-hand column with `sticky top-4`, so "Jetzt bezahlen" is on screen
// for the entire visit. Below the lg breakpoint the grid collapses and the
// columns stack in DOM order: every gift card personalization block first
// (_prepurchase_gift_card is 600+ lines and _personalize_fields exposes up to
// two dozen inputs per card), and only then the payment section. `sticky top-4`
// does nothing for a full-width stacked block, so the mobile buyer has to
// scroll past the whole form to find the button the desktop buyer never loses
// sight of.
//
// GA4 (60 days, atento hosts) puts mobile at 75% of sessions and converts it at
// roughly half the desktop rate, and about a third of that gap sits in
// add-to-cart -> purchase. This closes the part of it that is purely layout.
//
// The bar does not re-implement checkout. It clicks the real submit button, so
// every existing guard runs untouched: consent sync, the email check, the
// funnel/Meta/GA4 events and the spinner. When the email check rejects, the
// existing handler focuses and scrolls to the field, which turns this into a
// jump-to-what-is-missing affordance rather than a dead end.
export default class extends Controller {
  static targets = ["bar"]
  static values = {
    // Hide the bar once the real payment section is visible: while the better
    // version of this control is on screen, a floating duplicate is just
    // clutter covering content.
    watch: { type: String, default: "#mobile-summary" },
    offset: { type: Number, default: 240 }
  }

  connect() {
    this.visible = false
    this.paymentOnScreen = false
    this.frame = null

    const watched = document.querySelector(this.watchValue)
    if (watched && "IntersectionObserver" in window) {
      this.observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => { this.paymentOnScreen = entry.isIntersecting })
        this.refresh()
      }, { rootMargin: "0px 0px -25% 0px" })
      this.observer.observe(watched)
    }

    this.onScroll = () => {
      if (this.frame) return
      this.frame = window.requestAnimationFrame(() => {
        this.frame = null
        this.refresh()
      })
    }
    window.addEventListener("scroll", this.onScroll, { passive: true })
    window.addEventListener("resize", this.onScroll, { passive: true })
    this.refresh()
  }

  disconnect() {
    // Turbolinks keeps the JS context alive across navigations, so a controller
    // that does not unbind leaks a listener per visit.
    if (this.observer) this.observer.disconnect()
    if (this.frame) window.cancelAnimationFrame(this.frame)
    window.removeEventListener("scroll", this.onScroll)
    window.removeEventListener("resize", this.onScroll)
  }

  refresh() {
    this.toggle(window.scrollY > this.offsetValue && !this.paymentOnScreen)
  }

  toggle(show) {
    if (show === this.visible) return
    this.visible = show
    const bar = this.hasBarTarget ? this.barTarget : this.element
    bar.classList.toggle("translate-y-full", !show)
    bar.classList.toggle("opacity-0", !show)
    bar.classList.toggle("pointer-events-none", !show)
    bar.setAttribute("aria-hidden", show ? "false" : "true")
  }

  pay(event) {
    event.preventDefault()
    const button = document.getElementById("checkout-submit-button")
    if (!button) return
    button.click()
  }
}
